import { Button, Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import {
  deleteInvoiceArticleCategory,
  getInvoiceArticleCategoryById,
  updateInvoiceArticleCategory,
} from 'src/shared/services/invoiceArticleCategoryService';
import { InvoiceArticleCategoryRequest } from '../models/invoiceArticleCategoryRequest';

type EditArticleCategoryProps = {
  articleCategoryId: number;
  setArticleCategoryId: (resetArticleId: undefined) => void;
  setOpenEditCategory: (openEditArticle: boolean) => void;
  setRefetchDataGrid: (refetch: boolean) => void;
};

export const EditArticleCategory = (props: EditArticleCategoryProps) => {
  const { articleCategoryId, setArticleCategoryId, setOpenEditCategory, setRefetchDataGrid } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);

  const minimumCharRequired = 3;
  const maximumCharRequired = 20;
  const minCharRequiredMessage = t('common:validationMessages.minLength', { count: minimumCharRequired });
  const maxCharRequiredMessage = t('common:validationMessages.maxLength', { count: maximumCharRequired });

  const schema = yup.object({
    articleCategory_id: yup.number().required(),
    product_id: yup.number().required(),
    articleCategory: yup.string().min(minimumCharRequired, minCharRequiredMessage).max(maximumCharRequired, maxCharRequiredMessage).required(),
  });

  const formMethods = useForm<InvoiceArticleCategoryRequest>({
    resolver: yupResolver(schema),
    defaultValues: {
      articleCategory_id: articleCategoryId,
      product_id: 0,
      articleCategory: '',
    },
  });
  const { handleSubmit, setValue } = formMethods;

  const handleClose = () => {
    setArticleCategoryId(undefined);
    setOpenEditCategory(false);
  };

  const handleDelete = async () => {
    setLoading(true);

    try {
      await deleteInvoiceArticleCategory(articleCategoryId);
      if (isMounted()) {
        toast.success(<>{t('invoice:articleCategoryPage.deleteSuccess')}</>);
        handleClose();
        setRefetchDataGrid(true);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.success(<>{t('invoice:articleCategoryPage.error')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getInvoiceArticleCategoryById(articleCategoryId);

      if (isMounted()) {
        setValue('product_id', result.data.product_id);
        setValue('articleCategory', result.data.articleCategory);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [articleCategoryId, isMounted, setValue]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSubmit = async (data: InvoiceArticleCategoryRequest) => {
    setLoading(true);

    try {
      await updateInvoiceArticleCategory(data);
      if (isMounted()) {
        toast.success(<>{t('invoice:articleCategoryPage.updateSuccess')}</>);
        handleClose();
        setRefetchDataGrid(true);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.success(<>{t('invoice:articleCategoryPage.error')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form id='update-article-form' onSubmit={handleSubmit(onSubmit)}>
          <GenericDialog
            maxWidth='sm'
            open
            onClose={handleClose}
            onSave={handleSubmit(onSubmit)}
            dialogTitle={t('invoice:articleCategoryPage.editCategory')}
            dialogContent={
              <Grid container spacing={2}>
                <LoadingOverlay loading={loading} />
                <Grid item xs={12}>
                  <ControlledTextField fullWidth label={t('invoice:articleCategoryPage.category')} name='articleCategory' />
                </Grid>
              </Grid>
            }
            dialogActions={
              <Grid container justifyContent='space-between'>
                <Grid item>
                  <Button size='large' variant='outlined' color='error' onClick={() => setOpenDeleteDialog(true)}>
                    {t('common:button.delete')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    form='update-article-form'
                    size='large'
                    sx={{ marginLeft: 2 }}
                    disabled={loading}
                    disableElevation
                    variant='contained'
                    color='primary'
                    type='submit'
                  >
                    {t('common:button.save')}
                  </Button>
                </Grid>
              </Grid>
            }
          />
        </form>
      </FormProvider>

      <DeleteConfirmationDialog
        title={t('common:confirmDelete')}
        value=''
        modaltext={t('common:irreversibleAction')}
        onClose={(value) => {
          if (value) {
            handleDelete();
          } else {
            setOpenDeleteDialog(false);
          }
        }}
        open={openDeleteDialog}
      />
    </>
  );
};
